/**
 * Enum of all feature gate keys used in oauth-consent.
 *
 * Each entry should include a comment linking to the Switcheroo gate URL.
 * Example:
 *   // https://switcheroo.atlassian.com/ui/gates/<gate-id>/key/<gate-key>
 *   MY_FEATURE = 'oauth-consent-my-feature',
 */
export enum OAuthConsentFeatureGate {
  // Gates Trello consent screen access to app authors/collaborators (Horsefly program).
  // https://switcheroo.atlassian.com/ui/gates/7d71dd9e-26b8-4587-b202-8ef2814277a1/key/oauth-consent_trello-contributor-gate
  TRELLO_CONTRIBUTOR_GATE = 'oauth-consent_trello-contributor-gate',

  // Internal bypass for site selector when /consent/info returns no workspaces (e.g. timeout).
  // NOT intended for production use — shown only to internal users via this gate.
  SITE_SELECTOR_BYPASS = 'oauth-consent_site-selector-bypass',

  // Sorts the workspace list in MCP-style site selectors.
  MCP_SORT_WORKSPACES = 'oauth-consent_mcp-sorted-workspaces',

  // Enables selecting more than one Trello workspace on the MCP consent screen.
  TRELLO_MCP_MULTI_WORKSPACE_SELECTION = 'oauth-consent_trello-mcp-multi-workspace',

  // Enables the Trello workspace selector alongside the Atlassian site selector
  // in mixed-scope MCP consent flows. When disabled, the Trello workspace
  // selector is hidden even if hasTrelloProductAccess is true and Trello scopes
  // are present.
  TRELLO_MCP_MIXED_WORKSPACE = 'oauth-consent_trello-mcp-mixed-workspace',

  // Enables the reworked Trello multi-workspace selector on the MCP consent
  // screen, and the multi-workspace data and selection rules behind it.
  MULTI_WORKSPACE_SELECTOR = 'oauth-consent_multi-workspace-selector',

  // Enables the Bitbucket workspace selector on the MCP consent screen. When on,
  // availableBitbucketWorkspaces from /consent/info is rendered as a multi-select
  // dropdown above the Atlassian site selector.
  BITBUCKET_MCP_WORKSPACE = 'oauth-consent_bitbucket-mcp-workspace',

  // Replaces the MCP consent screen with the optional-site-selection flow in
  // MainContent/MCP-optional-sites: sites come from `availableSites`, and a
  // site, a Trello workspace and a Bitbucket workspace are each optional, with
  // Accept gated on permissions being rendered and selected. Off (the default)
  // keeps the flow in MainContent/MCP. Trello consent always uses the default
  // flow — see McpConsentSwitch.
  MCP_OPTIONAL_SITES = 'oauth-consent_mcp-optional-sites',

  // Shows the Jira "Delete" intent group on the MCP permissions list. Owned by
  // the MCP service, so the key does not follow the oauth-consent_ convention.
  MCP_JIRA_DELETE_TOOLSETS = 'atlassian_mcp_jira_delete_toolsets',

  // Shows the Jira "Manage" intent group on the MCP permissions list. Owned by
  // the MCP service, so the key does not follow the oauth-consent_ convention.
  MCP_JIRA_MANAGE_TOOLSETS = 'atlassian_mcp_jira_admin_toolsets',
}
